import { Button } from '@/components/ui/button'
import * as Field from '@/components/ui/field'
import * as FileUpload from '@/components/ui/file-upload'
import { Heading } from '@/components/ui/heading'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { client } from '@api'
import { createForm, valiForm } from '@modular-forms/solid'
import { Divider, Grid } from 'styled-system/jsx'
import { type InferInput, minLength, object, pipe, regex, string } from 'valibot'; // <-- Import Valibot
// import { orpc } from '@/lib/orpc'

// 1. Define your Valibot schema
const UrlSchema = object({
  url: pipe(
    string('Please enter a URL.'),
    minLength(1, 'Please enter a URL.'),
    regex(
      /^https?:\/\/(?:[a-zA-Z0-9-]+\.)*tradingview\.com(?:\/.*)?$/,
      'Must be a valid TradingView snapshot link'
    )
  ),
})

// 2. Infer the type directly from the schema so you don't repeat yourself!
type UrlForm = InferInput<typeof UrlSchema>

export const Upload = (props: {
  onLoadImage: (imageUrl: string | undefined) => void
  disabled?: boolean
}) => {
  // 3. Attach the schema to the form via the valibotForm adapter
  const [form, { Form, Field: FormField }] = createForm<UrlForm>({
    validate: valiForm(UrlSchema),
  })

  const handleImageUpload = (event: Event) => {
    const target = event.target as HTMLInputElement
    if (target.files?.[0]) {
      const file = target.files[0]
      props.onLoadImage(URL.createObjectURL(file))
    }
  }

  const handleImageUrlSubmit = async ({ url }: UrlForm) => {
    if (!url) return

    let targetUrl = url;
    const shareMatch = url.match(/\/x\/([a-zA-Z0-9]+)\/?/);

    if (shareMatch) {
      const id = shareMatch[1];
      targetUrl = `https://s3.tradingview.com/snapshots/${id.charAt(0).toLowerCase()}/${id}.png`;
    }

    try {
      let response = await fetch(targetUrl)

      if (!response.ok && shareMatch) {
        console.warn('Fast S3 guess failed, falling back to ORPC backend...');
        const result = await client.resolveTradingViewSnpashotUrl({ url });
        response = await fetch(result.url);
      }

      if (!response.ok) {
        throw new Error('Failed to download image blob from S3');
      }

      const blob = await response.blob()
      props.onLoadImage(URL.createObjectURL(blob))
    } catch (error) {
      console.error('Error handling TradingView image:', error)
    }
  }

  return (
    <Grid gap={3} alignContent='center' alignItems='center'>
      <Heading textAlign='center' maxW='md' mb='6' textStyle='xl'>To get started with the Price Data Extractor, upload an image from the HVF Draw tool</Heading>
      <Grid gap={2} justifyContent='center'>
        <FileUpload.Root
          maxFiles={1}
          onChange={handleImageUpload}
        >
          <FileUpload.Trigger asChild={triggerProps => <Button
            {...triggerProps}
            disabled={props.disabled}
            colorPalette="jade.10"
            variant="subtle"
            width={'100%'}
          >
            Upload Image
          </Button>} />
          <FileUpload.HiddenInput />
        </FileUpload.Root>

        <Text textAlign='center' children="Or" />

        {/* The destructured <Form> component handles binding automatically */}
        <Form onSubmit={handleImageUrlSubmit}>
          {/* 4. Removed inline validators from FormField! */}
          <FormField name="url">
            {(field, formFieldProps) => (
              <Field.Root invalid={!!field.error}>
                <Input
                  type="text"
                  {...formFieldProps}
                  value={field.value || ''} /* Fallback to empty string for safety */
                  placeholder="https://www.tradingview.com/x/..."
                />
                <Field.ErrorText>{field.error}</Field.ErrorText>
              </Field.Root>
            )}
          </FormField>

          <Button
            w="100%"
            type='submit'
            mt={2}
            disabled={props.disabled || form.submitting}
            colorPalette="jade.10"
            variant="subtle"
          >
            {form.submitting ? 'Loading...' : 'Load Image from Tradingview'}
          </Button>
        </Form>
      </Grid>
    </Grid>
  )
}
